import React, { createContext, useContext, useEffect, useState, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useNavigate } from 'react-router-dom'
import { logError } from '../lib/betterstack-logger'
import { getUrlAsync } from '../lib/service-urls'

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userFeatures, setUserFeatures] = useState([])
  // The user id whose features have finished loading (success or failure).
  // Until it matches the signed-in user, `featuresLoading` is true so a
  // feature-gated route can wait instead of redirecting on the empty list
  // (OPS-1406: deep links to /integrations/shipping/* bounced to /integrations).
  const [featuresUserId, setFeaturesUserId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [sessionError, setSessionError] = useState(null)

  // Load user features from database when user changes
  useEffect(() => {
    let current = true
    async function loadFeatures() {
      if (!user?.id) {
        setUserFeatures([])
        setFeaturesUserId(null)
        return
      }
      // Direct account switch A→B: drop A's flags before B's load so a
      // failed query can never mark B loaded with A's features (fail closed).
      setUserFeatures([])
      try {
        const { data, error } = await supabase
          .from('user_features')
          .select('feature')
          .eq('user_id', user.id)
          .eq('enabled', true)
        if (current) {
          setUserFeatures(!error && Array.isArray(data) ? data.map(f => f.feature) : [])
        }
      } catch (e) {
        if (current) setUserFeatures([])
        console.warn('Failed to load user features:', e)
        logError('user-features-load-failed', { error: e.message })
      } finally {
        if (current) {
          setFeaturesUserId(user.id)
        }
      }
    }
    loadFeatures()
    return () => {
      current = false
    }
  }, [user?.id])
  const [authTimedOut, setAuthTimedOut] = useState(false)
  const [retryCount, setRetryCount] = useState(0)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const retryTimeoutRef = useRef(null)
  const hardTimeoutRef = useRef(null)
  const authCompletedRef = useRef(false)
  const lastProactiveRefreshRef = useRef(0)
  // Tracks the currently-cached user id so we can wipe the React Query cache
  // when the identity actually changes (logout, or login as a different user
  // in the same tab). Without this, account A's cached data — including
  // is_admin — bleeds into account B's session (OPS-591). null = signed out.
  const previousUserIdRef = useRef(null)

  const MAX_RETRIES = 3
  const SOFT_TIMEOUT_MS = 10000  // 10 seconds before first retry
  const HARD_TIMEOUT_MS = 15000  // 15 seconds absolute max
  // Proactive-refresh guardrails
  const PROACTIVE_REFRESH_MIN_GAP_MS = 10000   // 10s debounce between explicit refreshes
  const PROACTIVE_REFRESH_THRESHOLD_SEC = 300  // refresh if < 5 min to expiry

  // Manual session refresh function (kept for API 401 retry use)
  const refreshSession = async () => {
    try {
      console.log('🔄 Refreshing session...')
      const { data: { session }, error } = await supabase.auth.refreshSession()
      
      if (error) {
        console.error('❌ Session refresh failed:', error)
        logError('auth-refresh-failed', { error: error.message })
        setSessionError(error.message)
        return { success: false, error }
      }

      if (session) {
        console.log('✅ Session refreshed successfully')
        setUser(session.user)
        setSessionError(null)
        return { success: true, session }
      } else {
        console.warn('⚠️ No session returned from refresh')
        setSessionError('No session available')
        return { success: false, error: new Error('No session available') }
      }
    } catch (error) {
      console.error('❌ Session refresh error:', error)
      logError('auth-refresh-error', { error: error.message })
      setSessionError(error.message)
      return { success: false, error }
    }
  }

  // Proactive session refresh on tab visibility / focus changes.
  //
  // Chrome aggressively throttles setTimeout/setInterval in background tabs,
  // which breaks Supabase's autoRefreshToken for long-backgrounded tabs.
  // When the tab comes back to the foreground, we check if the access token
  // is near expiry (or already expired) and explicitly refresh it.
  //
  // Guardrails:
  //   - Debounced to at most one explicit refresh per 10s (covers the common
  //     case where visibilitychange + focus fire back-to-back).
  //   - Soft failure: if refresh fails we log it and rely on the 401 retry
  //     logic in fetchWithAuth / railway-api for recovery — we do NOT eagerly
  //     bounce the user to /login from here.
  useEffect(() => {
    const maybeRefresh = async (trigger) => {
      // Only when tab is visible — focus can fire while minimized on some OSes.
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return

      const now = Date.now()
      if (now - lastProactiveRefreshRef.current < PROACTIVE_REFRESH_MIN_GAP_MS) {
        return // debounced
      }
      // Claim the slot SYNCHRONOUSLY before awaiting anything so a burst of
      // tightly-spaced visibilitychange+focus events can't all pass the gap
      // check before any of them has had a chance to write the ref.
      lastProactiveRefreshRef.current = now

      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        if (error || !session) return // not logged in or no session to refresh

        const expiresAt = session.expires_at || 0
        const nowSec = Math.floor(Date.now() / 1000)
        const secondsUntilExpiry = expiresAt - nowSec

        if (secondsUntilExpiry > PROACTIVE_REFRESH_THRESHOLD_SEC) return // plenty of time

        console.log(`🔄 Proactive refresh (${trigger}) — ${secondsUntilExpiry}s until expiry`)

        const { error: refreshError } = await supabase.auth.refreshSession()
        if (refreshError) {
          console.warn('⚠️ Proactive refresh failed:', refreshError.message)
          logError('auth-proactive-refresh-failed', {
            trigger,
            error: refreshError.message,
            secondsUntilExpiry
          })
          // Intentionally do NOT redirect here — defer to fetchWithAuth's softer
          // handling the next time an API call is made.
        }
      } catch (err) {
        console.warn('⚠️ Proactive refresh threw:', err.message)
        logError('auth-proactive-refresh-error', { trigger, error: err.message })
      }
    }

    const onVisibility = () => { maybeRefresh('visibilitychange') }
    const onFocus = () => { maybeRefresh('focus') }

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVisibility)
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', onFocus)
    }

    return () => {
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVisibility)
      }
      if (typeof window !== 'undefined') {
        window.removeEventListener('focus', onFocus)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Retry auth check with exponential backoff
  const retryAuthCheck = async (attempt = 0) => {
    if (attempt >= MAX_RETRIES) {
      console.error('❌ Auth check failed after', MAX_RETRIES, 'retries')
      logError('auth-check-retries-exhausted', { maxRetries: MAX_RETRIES })
      setAuthTimedOut(true)
      setLoading(false)
      return
    }
    
    const backoffMs = Math.min(1000 * Math.pow(2, attempt), 4000) // 1s, 2s, 4s
    console.log(`🔄 Auth retry attempt ${attempt + 1}/${MAX_RETRIES} in ${backoffMs}ms...`)
    
    retryTimeoutRef.current = setTimeout(async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession()
        
        if (error) {
          console.warn('⚠️ Auth retry failed:', error.message)
          setRetryCount(attempt + 1)
          retryAuthCheck(attempt + 1)
          return
        }
        
        if (session) {
          console.log('✅ Auth retry succeeded')
          authCompletedRef.current = true
          if (hardTimeoutRef.current) {
            clearTimeout(hardTimeoutRef.current)
            hardTimeoutRef.current = null
          }
          setUser(session.user)
          setSessionError(null)
          setAuthTimedOut(false)
          setLoading(false)
        } else {
          // No session but no error - user might not be logged in
          console.log('ℹ️ Auth retry: No session (user not logged in)')
          authCompletedRef.current = true
          if (hardTimeoutRef.current) {
            clearTimeout(hardTimeoutRef.current)
            hardTimeoutRef.current = null
          }
          setLoading(false)
        }
      } catch (err) {
        console.warn('⚠️ Auth retry error:', err.message)
        logError('auth-retry-error', { error: err.message, attempt: attempt + 1 })
        setRetryCount(attempt + 1)
        retryAuthCheck(attempt + 1)
      }
    }, backoffMs)
  }
  
  // Safety timeout with retry logic
  // Soft timeout (10s): Start retry attempts
  // Hard timeout (15s): Force render with error state
  useEffect(() => {
    const softTimeout = setTimeout(() => {
      if (loading && !user) {
        console.warn('⚠️ Auth soft timeout — starting retry attempts')
        retryAuthCheck(0)
      }
    }, SOFT_TIMEOUT_MS)
    
    hardTimeoutRef.current = setTimeout(() => {
      if (!authCompletedRef.current) {
        console.error('❌ Auth hard timeout — forcing render with error state')
        logError('auth-hard-timeout', { timeoutMs: HARD_TIMEOUT_MS })
        setAuthTimedOut(true)
        setLoading(false)
      }
    }, HARD_TIMEOUT_MS)
    
    return () => {
      clearTimeout(softTimeout)
      if (hardTimeoutRef.current) {
        clearTimeout(hardTimeoutRef.current)
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  
  // Manual retry function for error state button
  const handleManualRetry = async () => {
    setLoading(true)
    setAuthTimedOut(false)
    setRetryCount(0)
    setSessionError(null)
    
    try {
      const { data: { session }, error } = await supabase.auth.getSession()
      
      if (error) throw error
      
      if (session) {
        setUser(session.user)
      }
      setLoading(false)
    } catch (err) {
      console.error('❌ Manual retry failed:', err.message)
      logError('auth-manual-retry-failed', { error: err.message })
      setSessionError(err.message)
      setAuthTimedOut(true)
      setLoading(false)
    }
  }

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('❌ Error getting initial session:', error)
        logError('auth-initial-session-failed', { error: error.message })
        setSessionError(error.message)
        setLoading(false)
        return
      }

      if (session) {
        console.log('✅ Initial session loaded')
        authCompletedRef.current = true
        if (hardTimeoutRef.current) {
          clearTimeout(hardTimeoutRef.current)
          hardTimeoutRef.current = null
        }
        // Fresh page load — the cache is already empty, so just record the
        // identity baseline; don't clear (would throw away the first paint's data).
        previousUserIdRef.current = session.user.id
        setUser(session.user)
        setSessionError(null)
      } else {
        console.log('ℹ️ No initial session found')
        authCompletedRef.current = true
        if (hardTimeoutRef.current) {
          clearTimeout(hardTimeoutRef.current)
          hardTimeoutRef.current = null
        }
        previousUserIdRef.current = null
        setUser(null)
      }
      
      setLoading(false)
    })

    // Listen for auth changes with enhanced event handling
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('🔐 Auth state change:', event, session?.user?.email || 'no user')

      switch (event) {
        case 'INITIAL_SESSION':
          // Initial session loaded
          if (session) {
            previousUserIdRef.current = session.user.id
            setUser(session.user)
            setSessionError(null)
          }
          break

        case 'SIGNED_IN':
          // User signed in
          if (session) {
            console.log('✅ User signed in:', session.user.email)
            // If a *different* user was previously active in this tab (account
            // switch, or login over a stale session), wipe the React Query cache
            // so the new user never sees the previous user's data (OPS-591).
            if (
              previousUserIdRef.current &&
              previousUserIdRef.current !== session.user.id
            ) {
              console.log('🧹 Account switch — clearing query cache')
              queryClient.clear()
            }
            previousUserIdRef.current = session.user.id
            setUser(session.user)
            setSessionError(null)
          }
          break

        case 'SIGNED_OUT':
          // User signed out — drop all cached per-user data so the next account
          // to log in starts from a clean slate (OPS-591).
          console.log('👋 User signed out')
          queryClient.clear()
          previousUserIdRef.current = null
          setUser(null)
          setSessionError(null)
          navigate('/login')
          break

        case 'TOKEN_REFRESHED':
          // Token was refreshed
          console.log('🔄 Token refreshed successfully')
          if (session) {
            setUser(session.user)
            setSessionError(null)
          } else {
            console.warn('⚠️ Token refreshed but no session available')
            logError('auth-token-refresh-no-session', { event: 'TOKEN_REFRESHED' })
            setSessionError('Token refresh failed')
          }
          break

        case 'USER_UPDATED':
          // User data was updated
          console.log('👤 User updated')
          if (session) {
            setUser(session.user)
          }
          break

        case 'PASSWORD_RECOVERY':
          // Password recovery email sent
          console.log('🔑 Password recovery initiated')
          break

        default:
          console.log('ℹ️ Unhandled auth event:', event)
      }

      setLoading(false)
    })

    // Cleanup on unmount
    return () => {
      subscription.unsubscribe()
    }
  }, [navigate, queryClient])

  const signUp = async (email, password, userData) => {
    try {
      setSessionError(null)
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: window.location.origin
        }
      })

      if (error) throw error

      // Create user profile
      if (data.user) {
        const { error: profileError } = await supabase
          .from('users')
          .insert({
            user_id: data.user.id,
            email: data.user.email,
            name: userData.name || email.split('@')[0]
          })

        if (profileError) {
          console.warn('⚠️ Profile creation failed (may already exist):', profileError)
        }
      }

      return data
    } catch (error) {
      setSessionError(error.message)
      throw error
    }
  }

  const signIn = async (email, password) => {
    try {
      setSessionError(null)
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      })

      if (error) throw error
      
      // Session health check will be started by onAuthStateChange
      return data
    } catch (error) {
      setSessionError(error.message)
      throw error
    }
  }

  const signOut = async () => {
    try {
      setSessionError(null)

      const { error } = await supabase.auth.signOut()
      if (error) throw error

      // Wipe cached per-user data immediately (the SIGNED_OUT event also does
      // this, but clearing here too avoids any window where stale data is shown).
      queryClient.clear()
      previousUserIdRef.current = null
      setUser(null)
    } catch (error) {
      setSessionError(error.message)
      throw error
    }
  }

  const resetPassword = async (email) => {
    // OpSyncPro owns transactional email (Resend). The recovery link is minted
    // + sent by auth-service, NOT Supabase's mailer. Neutral by design: the
    // endpoint always 200s so we never reveal whether the account exists.
    try {
      setSessionError(null)
      const authUrl = (await getUrlAsync('AUTH_SERVICE_URL')) || ''
      const res = await fetch(`${authUrl}/api/auth/password/reset-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() }),
      })
      if (!res.ok) throw new Error('reset_request_failed')
    } catch (error) {
      setSessionError(error.message)
      throw error
    }
  }

  const value = {
    // Only expose flags loaded for *this* user id — never a previous account's.
    user: user ? { ...user, features: featuresUserId === user.id ? userFeatures : [] } : null,
    loading,
    featuresLoading: Boolean(user?.id) && featuresUserId !== user.id,
    sessionError,
    authTimedOut,
    signUp,
    signIn,
    signOut,
    resetPassword,
    refreshSession,
    handleManualRetry,
    isAuthenticated: !!user
  }

  // Show loading indicator instead of rendering nothing
  // (rendering nothing with a dark background = black screen)
  if (loading) {
    return (
      <AuthContext.Provider value={value}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: '#1a1a1a',
          color: '#f97316'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '3px solid #333',
              borderTopColor: '#f97316',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
              margin: '0 auto 16px'
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            <div style={{ fontSize: '14px', opacity: 0.7 }}>
              {retryCount > 0 ? `Retrying... (${retryCount}/${MAX_RETRIES})` : 'Loading...'}
            </div>
          </div>
        </div>
      </AuthContext.Provider>
    )
  }
  
  // Show error state with retry button when auth times out
  if (authTimedOut && !user) {
    return (
      <AuthContext.Provider value={value}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          background: '#1a1a1a',
          color: '#fff'
        }}>
          <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
            <div style={{
              width: '60px',
              height: '60px',
              borderRadius: '50%',
              background: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px',
              fontSize: '28px'
            }}>
              !
            </div>
            <h2 style={{ fontSize: '20px', marginBottom: '12px', color: '#f97316' }}>
              Connection Issue
            </h2>
            <p style={{ fontSize: '14px', opacity: 0.7, marginBottom: '20px', lineHeight: 1.5 }}>
              Unable to connect to the server. This might be a temporary network issue.
              {sessionError && <><br /><span style={{ color: '#ef4444' }}>{sessionError}</span></>}
            </p>
            <button
              onClick={handleManualRetry}
              style={{
                background: '#f97316',
                color: '#fff',
                border: 'none',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                marginRight: '12px'
              }}
            >
              Try Again
            </button>
            <button
              onClick={() => navigate('/login')}
              style={{
                background: 'transparent',
                color: '#f97316',
                border: '1px solid #f97316',
                padding: '12px 24px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              Go to Login
            </button>
          </div>
        </div>
      </AuthContext.Provider>
    )
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
